import React, { useEffect, useState } from 'react';
import { BellOff, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { alertHealthAPI } from '../lib/api';

// Every alert surface (tap alerts, new-enquiry broadcast, F&I and trade desks)
// fans out to a recipient list. An empty list is not an error — the send is
// skipped and logged, silently — so a dealership that never configured one
// experiences a product that simply never speaks. This is the one place that
// says so out loud. Renders NOTHING once at least one recipient exists.
export const AlertsDeafBanner = ({ organizationId, onFix, compact = false }) => {
    const { t } = useTranslation();
    const [health, setHealth] = useState(null);

    useEffect(() => {
        let alive = true;
        if (!organizationId) { setHealth(null); return undefined; }
        alertHealthAPI.get(organizationId)
            .then((r) => { if (alive) setHealth(r.data); })
            .catch(() => { if (alive) setHealth(null); });
        return () => { alive = false; };
    }, [organizationId]);

    // Out of scope when the dealership has no inventory yet — nothing to alert
    // on, so silence is correct and a banner would just be noise.
    if (!health || !health.alerts_relevant || health.alerts_configured) return null;

    return (
        <div className={`rounded-xl border border-amber-500/40 bg-amber-500/10 flex items-start gap-3 ${compact ? 'p-3' : 'p-4'}`}
            data-testid="alerts-deaf-banner">
            <BellOff className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-200" data-testid="alerts-deaf-banner-title">
                    {t('alertHealth.title', 'Nobody is receiving your alerts')}
                </p>
                <p className="text-xs text-amber-100/80 mt-1 leading-relaxed" data-testid="alerts-deaf-banner-body">
                    {health.deaf_vehicles > 0
                        ? t('alertHealth.bodyVehicles', 'Taps, enquiries and test-drive requests on {{count}} vehicles are being recorded but sent to nobody. Add one mobile number and they start arriving.', { count: health.deaf_vehicles })
                        : t('alertHealth.body', 'Taps, enquiries and test-drive requests are being recorded but sent to nobody. Add one mobile number and they start arriving.')}
                </p>
                {onFix ? (
                    <button onClick={onFix} data-testid="alerts-deaf-banner-fix"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-200 hover:text-white transition-colors">
                        {t('alertHealth.fix', 'Add a recipient')}<ArrowRight className="w-3.5 h-3.5" />
                    </button>
                ) : (
                    <p className="mt-2 text-xs font-semibold text-amber-200" data-testid="alerts-deaf-banner-hint">
                        {t('alertHealth.fixHere', 'Add one below to switch them on.')}
                    </p>
                )}
            </div>
        </div>
    );
};

export default AlertsDeafBanner;
